'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';

interface Props {
    mode?: 'login' | 'agree';
    onConfirm: () => void;
    onClose?: () => void;
}

const KickLogo = () => (
    <svg className="w-6 h-6 text-white" viewBox="0 0 512 512" fill="currentColor">
        <path d="M37 .036h164.448v113.621h54.71v-56.82h54.731V.036h164.448v170.777h-54.73v56.82h-54.711v56.8h54.71v56.82h54.73V512.03H310.89v-56.82h-54.73v-56.8h-54.711v113.62H37V.036z" />
    </svg>
);

function Checkbox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5 shrink-0">
                <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                    checked ? 'bg-emerald-500 border-emerald-500' : 'border-gray-600 group-hover:border-gray-400'
                }`}>
                    {checked && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>
            </div>
            <span className="text-sm text-gray-300 leading-snug">
                I confirm I&apos;m over <span className="text-white font-semibold">18 years old</span> and that I have read the{' '}
                <Link
                    href="/terms"
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors duration-200"
                >
                    Terms and Conditions
                </Link>
            </span>
        </label>
    );
}

export default function AgeVerificationModal({ mode = 'login', onConfirm, onClose }: Props) {
    const [agreed, setAgreed] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => setMounted(true), []);
    if (!mounted) return null;

    const overlay = (
        <div
            className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-gray-900 shadow-2xl rounded-2xl w-full max-w-sm my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6 shrink-0">
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors duration-200"
                        >
                            <span className="text-2xl font-bold leading-none">×</span>
                        </button>
                    )}
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                            <KickLogo />
                        </div>
                        <div>
                            {mode === 'login' ? (
                                <>
                                    <h2 className="text-2xl font-bold text-white">LOG IN</h2>
                                    <p className="text-green-100 text-sm mt-0.5">Sign in with your Kick account</p>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-bold text-white">SokoPlay</h2>
                                    <p className="text-green-100 text-sm mt-0.5">Before you start playing</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5 overflow-y-auto">
                    <Checkbox checked={agreed} onChange={setAgreed} />

                    {mode === 'login' ? (
                        <button
                            onClick={() => { if (agreed) onConfirm(); }}
                            disabled={!agreed}
                            className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl font-bold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-emerald-500/20 hover:shadow-lg text-white"
                        >
                            <KickLogo />
                            Continue with Kick
                        </button>
                    ) : (
                        <button
                            onClick={() => { if (agreed) onConfirm(); }}
                            disabled={!agreed}
                            className="w-full py-3 rounded-xl font-bold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-emerald-500/20 hover:shadow-lg text-white"
                        >
                            I Agree — Start Playing
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(overlay, document.body);
}
